/**
 * O erro fica DEBAIXO DO CAMPO ERRADO — e isso exige um mapa, não uma string.
 *
 * Os cinco formulários deste módulo guardavam uma única `string | null` de erro
 * e a exibiam num `<Campo>` só: o PRIMEIRO da tela. Quem esquecia o produto
 * lia "Escolha o produto aplicado." debaixo de "Equipamento", que já estava
 * preenchido. Pior no leitor de tela: o `aria-describedby` também apontava para
 * o campo errado, e o anúncio saía "Volume observado, inválido: Escolha o
 * tanque na tabela ao lado".
 *
 * O próprio componente `Campo` nomeia a falha no cabeçalho dele: "Erro ABAIXO
 * DO CAMPO, nunca em alerta no topo. Num cadastro de quinze campos, alerta no
 * topo obriga a pessoa a descobrir sozinha qual deles a mensagem está
 * descrevendo." Uma string única é a mesma falha com outro desenho — o alerta
 * do topo mudou de lugar, não de natureza.
 *
 * `FormularioAbastecimento` já fazia certo, com o `MapaDeErros` dele: confere
 * ao sair do campo e, no envio, mostra TODOS os erros de uma vez. Este
 * arquivo é a mesma ideia no tamanho que os outros formulários precisam, e mora
 * fora deles porque são cinco formulários, e cinco cópias da mesma decisão
 * divergem na primeira correção.
 */
export type ErrosDeCampo = Readonly<Record<string, string>>;

/** Nada errado ainda. Constante para não criar objeto novo a cada render. */
export const NENHUM_ERRO: ErrosDeCampo = Object.freeze({});

/**
 * O erro de UM campo trocado, e os outros como estavam.
 *
 * É o que o `onBlur` usa (p.16 e R01 p.28: o erro aparece abaixo do campo AO
 * SAIR DELE) e o que o `onChange` usa para parar de apontar o problema que a
 * pessoa já está consertando. `null` apaga o erro do campo.
 */
export function comErroDe(erros: ErrosDeCampo, campo: string, mensagem: string | null): ErrosDeCampo {
  const resto: Record<string, string> = {};
  for (const [chave, valor] of Object.entries(erros)) {
    if (chave !== campo) resto[chave] = valor;
  }
  if (mensagem !== null) resto[campo] = mensagem;
  return resto;
}

/**
 * Todos os campos conferidos de uma vez, no envio.
 *
 * Um erro por campo, cada um debaixo do seu — e não o primeiro da lista: com
 * três campos em branco, mostrar um só obrigava a enviar três vezes para
 * descobrir os três.
 */
export function conferirTodos(campos: readonly string[], mensagemDe: (campo: string) => string | null): ErrosDeCampo {
  let erros: ErrosDeCampo = NENHUM_ERRO;
  for (const campo of campos) erros = comErroDe(erros, campo, mensagemDe(campo));
  return erros;
}

/** Há algum erro no mapa. */
export function temErro(erros: ErrosDeCampo): boolean {
  return Object.keys(erros).length > 0;
}

/**
 * As três coisas que todo campo com erro precisa, montadas juntas.
 *
 * Juntas de propósito: separadas, é possível passar `erro` ao `<Campo>` e
 * esquecer o `aria-invalid` do `<input>` — que é o defeito de acessibilidade
 * ficando invisível para quem enxerga a tela.
 */
export function marcaDeErro(
  erros: ErrosDeCampo,
  campo: string,
  idDoCampo: string,
): { readonly 'aria-invalid': true; readonly 'aria-describedby': string } | Record<string, never> {
  return erros[campo] ? { 'aria-invalid': true, 'aria-describedby': `${idDoCampo}-erro` } : {};
}
